import React, { useEffect, useState, useCallback } from "react";

interface KeyboardShortcutsProps {
  onCopyLastResponse?: () => void;
  onNewChat?: () => void;
}

const SHORTCUTS = [
  { keys: ["Enter"], desc: "Send message" },
  { keys: ["Shift", "Enter"], desc: "New line" },
  { keys: ["Cmd", "K"], desc: "Focus input" },
  { keys: ["Cmd", "Shift", "C"], desc: "Copy last response" },
  { keys: ["Cmd", "/"], desc: "Show shortcuts" },
  { keys: ["Cmd", "N"], desc: "New conversation" },
  { keys: ["Escape"], desc: "Close overlay / Cancel" },
];

export default function KeyboardShortcuts({
  onCopyLastResponse,
  onNewChat,
}: KeyboardShortcutsProps) {
  const [open, setOpen] = useState(false);

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.userAgent);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;

      // Cmd+/ — toggle shortcuts
      if (mod && e.key === "/") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }

      // Cmd+Shift+C — copy last response
      if (mod && e.shiftKey && e.key === "C") {
        e.preventDefault();
        onCopyLastResponse?.();
        return;
      }

      // Cmd+N — new chat
      if (mod && e.key === "n") {
        e.preventDefault();
        onNewChat?.();
        return;
      }

      // Escape — close overlay
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    },
    [isMac, open, onCopyLastResponse, onNewChat],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={() => setOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} />

      {/* Modal */}
      <div
        className="relative rounded-xl p-6 shadow-lg max-w-sm w-full mx-4"
        style={{
          backgroundColor: "var(--c-surface)",
          border: "1px solid var(--c-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
            Keyboard Shortcuts
          </h3>
          <button
            onClick={() => setOpen(false)}
            className="p-1 rounded"
            style={{ color: "var(--c-text-faint)" }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </button>
        </div>

        <div className="space-y-2">
          {SHORTCUTS.map((shortcut) => (
            <div key={shortcut.desc} className="flex items-center justify-between">
              <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                {shortcut.desc}
              </span>
              <div className="flex items-center gap-1">
                {shortcut.keys.map((key) => (
                  <kbd
                    key={key}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                    style={{
                      backgroundColor: "var(--c-surface-2)",
                      border: "1px solid var(--c-border)",
                      color: "var(--c-text-secondary)",
                    }}
                  >
                    {key === "Cmd" ? (isMac ? "⌘" : "Ctrl") : key}
                  </kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
